/**
 * Event Catalog & Mapping
 *
 * Central definition of all tracking events with platform-specific mappings.
 * This ensures consistency across all tracking destinations.
 */

import type { TrackingEventName } from './types';

/**
 * Meta Pixel standard events
 * @see https://developers.facebook.com/docs/meta-pixel/reference
 */
export const MetaStandardEvents = {
  // Page/Content
  page_view: 'PageView',
  viewed_product: 'ViewContent',

  // Search
  search: 'Search',

  // E-commerce
  added_to_cart: 'AddToCart',
  added_to_wishlist: 'AddToWishlist',
  started_checkout: 'InitiateCheckout',
  added_payment_info: 'AddPaymentInfo',
  placed_order: 'Purchase',

  // Lead Generation
  form_submitted: 'Lead',
  newsletter_subscribed: 'Subscribe',
  waitlist_form_submitted: 'Lead',
  preorder_started: 'Lead',

  // Registration
  account_created: 'CompleteRegistration',
} as const;

/**
 * Klaviyo standard metric names
 * @see https://developers.klaviyo.com/en/docs/guide-to-integrating-a-platform-without-a-pre-built-klaviyo-integration
 */
export const KlaviyoMetricNames = {
  // Page Views
  // Note: 'page_view' is NOT tracked - Klaviyo script does this automatically as 'Active on Site'
  // Manual tracking would create duplicates
  page_view: null, // Let Klaviyo script handle this
  viewed_waitlist_landing_page: 'Viewed Waitlist Landing Page',
  viewed_pricing_page: 'Viewed Pricing Page',
  viewed_product: 'Viewed Product',

  // Forms (Title Case for consistency)
  form_started: 'Form Started',
  form_submitted: 'Form Submitted',
  form_submission_failed: 'Form Submission Failed',
  email_entered: 'Email Entered',
  waitlist_form_started: 'Waitlist Form Started',
  waitlist_form_submitted: 'Waitlist Form Submitted',

  // E-commerce (Shopify standard)
  added_to_cart: 'Added to Cart',
  started_checkout: 'Started Checkout',
  placed_order: 'Placed Order',
  preorder_started: 'Preorder Started',

  // Engagement
  newsletter_subscribed: 'Newsletter Subscribed',
  account_created: 'Account Created',
} as const;

/**
 * GTM/GA4 event names (snake_case convention)
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events
 */
export const GTMEventNames = {
  // Page/Content
  page_view: 'page_view',
  viewed_product: 'view_item',

  // E-commerce
  added_to_cart: 'add_to_cart',
  started_checkout: 'begin_checkout',
  placed_order: 'purchase',

  // Engagement
  form_started: 'form_start',
  form_submitted: 'form_submit',
  form_submission_failed: 'form_error',
  newsletter_subscribed: 'newsletter_signup',
  button_clicked: 'button_click',
  link_clicked: 'link_click',
  video_played: 'video_play',

  // Lead Gen
  waitlist_form_submitted: 'generate_lead',
  account_created: 'sign_up',
} as const;

/**
 * Get the Meta standard event name for a tracking event
 */
export function getMetaEventName(eventName: TrackingEventName): string {
  return (
    MetaStandardEvents[eventName as keyof typeof MetaStandardEvents] ||
    eventName
  );
}

/**
 * Get the Klaviyo metric name for a tracking event
 * Returns null for events that should not be tracked manually (handled by Klaviyo script)
 */
export function getKlaviyoMetricName(
  eventName: TrackingEventName,
): string | null {
  const metricName =
    KlaviyoMetricNames[eventName as keyof typeof KlaviyoMetricNames];

  // Return null if explicitly set to null (e.g., page_view handled by script)
  if (metricName === null) {
    return null;
  }

  // Return the metric name or fall back to the event name
  return metricName || eventName;
}

/**
 * Get the GTM event name for a tracking event
 */
export function getGTMEventName(eventName: TrackingEventName): string {
  return GTMEventNames[eventName as keyof typeof GTMEventNames] || eventName;
}

/**
 * Check if an event is a Meta standard event (vs custom event)
 */
export function isMetaStandardEvent(eventName: TrackingEventName): boolean {
  return eventName in MetaStandardEvents;
}

/**
 * Event documentation for reference
 */
export const EventDocumentation = {
  page_view: {
    description: 'User viewed a page',
    properties: ['page_title', 'page_url', 'referrer'],
  },

  viewed_product: {
    description: 'User viewed a product page',
    properties: ['product_id', 'product_name', 'price', 'currency', 'category'],
  },

  added_to_cart: {
    description: 'User added item to shopping cart',
    properties: ['product_id', 'product_name', 'price', 'quantity', 'currency'],
  },

  started_checkout: {
    description: 'User initiated checkout process',
    properties: ['cart_total', 'item_count', 'currency'],
  },

  placed_order: {
    description: 'User completed a purchase',
    properties: [
      'order_id',
      'total',
      'subtotal',
      'tax',
      'shipping',
      'currency',
      'products',
    ],
  },

  waitlist_form_submitted: {
    description: 'User submitted waitlist form',
    properties: ['email', 'name', 'source_page'],
  },

  newsletter_subscribed: {
    description: 'User subscribed to newsletter',
    properties: ['email', 'list_id', 'source'],
  },
} as const;
